/**
 * THE CONSENT CONTEXT — the state the banner and Cookie settings read and write.
 *
 * It renders nothing itself. `ConsentBanner`, `ConsentFooterItems` and
 * `CookieSettings` are the visitor-facing surfaces; this is the one place that
 * decides what an answer means, what happens when it cannot be recorded, and
 * where focus goes when the control that opened a dialog has disappeared.
 *
 * THREE THINGS LIVE HERE THAT LOOK LIKE COMPONENT STATE AND ARE NOT.
 *
 *   `state`           the persisted answer. Durable, survives reload.
 *
 *   `settingsResult`  the result of the CURRENT Cookie settings interaction.
 *                     Transient by construction — it is React state, so a
 *                     reload clears it, and `ConsentFooterItems` clears it on
 *                     mount so a client-side navigation does too.
 *
 *                     This is what separates BC-03 from BC-06. The first
 *                     version derived the confirmation from
 *                     `state === "denied"`, so a first-time "Continue without
 *                     analytics" announced a WITHDRAWAL that had not happened,
 *                     and the same message reappeared on every later page and
 *                     after every reload. A confirmation is the result of an
 *                     action, not another reading of stored state.
 *
 *   `persistentControl`
 *                     a ref to the Cookie settings control in the page's own
 *                     footer, which does not unmount when an answer changes. It
 *                     is the focus destination whenever the control that opened
 *                     a dialog has gone — see the focus table in `Consent.tsx`.
 *
 *                     "On every eligible page" was written here before it was
 *                     true. `Footer` carries the control on five views and the
 *                     five BOOKING STEPS mount no `Footer`, so through the whole
 *                     flow this ref was never assigned — and `.focus()` on an
 *                     unassigned ref is a silent no-op, so focus stayed on
 *                     `<body>` and BC-06 had no host. `views/Book/StepChrome`
 *                     exports `StepMeta` for exactly this, and every step
 *                     renders it.
 *
 * NO MANUAL MEMOISATION. The React Compiler is enabled for this surface and
 * `apps/book/vite.config.ts` says new code should not reach for `useMemo` or
 * `useCallback` without a measured reason. The documented exception is
 * `TranslationProvider`, which builds four `Intl` formatters.
 */
import { createContext, useContext, useRef, useState, type ReactNode, type RefObject } from "react";
import {
  clearAnalyticsCookies,
  readConsent,
  writeConsent,
  type ConsentChoice,
  type ConsentState,
} from "./consent";
import { applyConsentChange } from "./session";
import { privacyNoticeUrl } from "./property";
import { isCredentialRoute } from "./routes";

/** Where an answer came from. Only a settings save confirms on screen. */
export type ConsentSource = "banner" | "settings";

export interface ConsentApi {
  readonly state: ConsentState;
  /**
   * Record an answer. Returns whether it was PERSISTED.
   *
   * A false here is what the "We couldn't save your setting" screen is for.
   * The in-memory state does not move on a failed write: a choice we could not
   * record must not behave as though we had, in either direction, and for
   * `granted` in particular the alternative is collecting on an answer that
   * exists nowhere.
   */
  readonly choose: (choice: ConsentChoice, source: ConsentSource) => boolean;
  /** The current settings interaction's saved answer, or null. Transient. */
  readonly settingsResult: ConsentChoice | null;
  /** Called by the footer on mount, so a navigation does not carry it over. */
  readonly clearSettingsResult: () => void;
  /** The footer's Cookie settings control — the surviving focus destination. */
  readonly persistentControl: RefObject<HTMLButtonElement | null>;
  /**
   * May this visitor be ASKED at all?
   *
   * False on a credential route, and false while Legal/Privacy has not
   * published the notice — a request whose terms cannot be shown is not a
   * request, and a notice link with nowhere to go is worse than none. Both are
   * checked here rather than in the components, so a second consent surface
   * cannot be built that forgets one.
   */
  readonly askable: boolean;
  /** The published notice, or null. Never a guessed path. */
  readonly privacyNotice: string | null;
}

const ConsentContext = createContext<ConsentApi | null>(null);

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConsentState>(() => readConsent());
  const [settingsResult, setSettingsResult] = useState<ConsentChoice | null>(null);
  const persistentControl = useRef<HTMLButtonElement | null>(null);

  const choose = (choice: ConsentChoice, source: ConsentSource): boolean => {
    const persisted = writeConsent(choice);
    if (!persisted) return false;
    setState(choice);
    setSettingsResult(source === "settings" ? choice : null);
    if (choice === "denied") {
      // Best-effort, and consent.ts is explicit that a page cannot guarantee
      // the cookie sweep. The guarantee is that the library stops using
      // analytics storage now and the next document loads nothing.
      clearAnalyticsCookies();
    }
    applyConsentChange(choice, window.location.pathname, window.location.hostname);
    return true;
  };

  const privacyNotice = privacyNoticeUrl();
  const askable = privacyNotice !== null && !isCredentialRoute(window.location.pathname);

  return (
    <ConsentContext.Provider
      value={{
        state,
        choose,
        settingsResult,
        clearSettingsResult: () => setSettingsResult(null),
        persistentControl,
        askable,
        privacyNotice,
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent(): ConsentApi {
  const value = useContext(ConsentContext);
  if (!value) throw new Error("useConsent must be used inside <ConsentProvider>");
  return value;
}

/**
 * The consent state, or null where no provider is mounted.
 *
 * The consent surfaces are ADDITIVE: a banner and a settings link beside a
 * booking page. They must never be the reason a screen fails to render, and
 * `Footer` is on six views whose existing tests mount them directly — the
 * throwing hook took every one of those down. A missing provider is a real
 * mistake, so it is caught where it matters instead: `consentMount.test.ts`
 * asserts the app's own tree provides it, which is the only place the answer
 * can be wrong in production.
 */
export function useOptionalConsent(): ConsentApi | null {
  return useContext(ConsentContext);
}
